<script setup lang="ts">
import { faXmark, faLessThan } from '@fortawesome/pro-light-svg-icons'

import {
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { preventWindowScroll, useVisualViewport } from '@shared/composables'
import { templateRef, whenever } from '@vueuse/core'
import type { AllowedComponentProps } from 'vue'

const open = defineModel<boolean>('open')
// eslint-disable-next-line no-spaced-func
const { withKeyboardPadding = true, onBack } = defineProps<{
  withKeyboardPadding?: boolean
  headerClass?: AllowedComponentProps['class']
  onBack?: (closeModal?: () => void) => void
}>()

const emit = defineEmits<{ remove: []; back: [] }>()

const contentWrapper = templateRef<HTMLElement>('contentWrapper')
const closeButtonEl = $ref<HTMLButtonElement>()
const { keyboardHeight, isKeyboardOpen } = $(useVisualViewport())

const keyboardPadding = $computed(() => {
  if (!withKeyboardPadding) return
  if (!isKeyboardOpen) return

  return keyboardHeight + 260
})

// Prevent window scroll when the modal is open
preventWindowScroll({ when: () => !!document.querySelector('dialog.fullscreen[open]') })

whenever(
  open,
  () => open.value && setTimeout(scrollTop, 100),
)

watchEffect(() => {
  if (!isKeyboardOpen || !withKeyboardPadding) scrollTop()
})

function onClose () {
  open.value = false
  // Delay emitting 'remove' until after the close animation
  setTimeout(() => {
    emit('remove')
  }, 500)
}

function scrollTop () {
  if (contentWrapper.value) {
    contentWrapper.value.scrollTop = 0
  }
}

defineExpose({
  scrollTop,
})
</script>

<template>
  <TransitionRoot :show="open" appear>
    <dialog
      class="fixed inset-0 w-full h-full bg-transparent backdrop:bg-transparent z-dialog fullscreen g-mobile-modal"
      open
    >
      <div class="relative w-full h-full">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div class="absolute inset-0 bg-navy bg-opacity-40" @click="() => onClose()"/>
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transform transition ease-out duration-300"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="transform transition ease-in duration-200"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <div
            ref="contentWrapper"
            class="
              fixed bottom-0 left-0 right-0 w-full bg-white rounded-t-v2lg
              overflow-y-auto modal-content-wrapper
            "
            style="max-height: calc(100svh - 1rem);"
          >
            <GIconButton
              ref="closeButtonEl"
              :icon="faXmark"
              type="button"
              size="small"
              class="absolute right-2 outline-none ring-0 z-10 top-5"
              @click="onClose"
            />

            <GIconButton
              v-if="onBack"
              :icon="faLessThan"
              type="button"
              size="small"
              class="absolute left-2 outline-none ring-0 z-10 top-5"
              @click="() => onBack && onBack(onClose)"
            />

            <div
              v-if="$slots.header"
              class="w-full pb-8 flex items-center justify-center font-semibold text-lg pt-4"
              :class="headerClass"
            >
              <slot name="header"/>
            </div>

            <div
              class="px-4 py-8 transition-all duration-300 ease-in-out modal-body g-mobile-modal-body"
              :class="{ '!pt-0': $slots.header }"
              :style="keyboardPadding ? `padding-bottom: ${keyboardPadding}px` : ''"
            >
              <slot/>
            </div>
            <button class="opacity-0 absolute bottom-0"/>
          </div>
        </TransitionChild>
      </div>
    </dialog>
  </TransitionRoot>
</template>
